const post = {
  slug: 'evaluating-llm-outputs',
  number: 10,
  title: 'How to Evaluate LLM Outputs Without Losing Your Mind',
  date: '2024-12-12',
  readTime: '7 min read',
  views: 34,
  category: 'ai',
  tags: ['AI/ML', 'LLM', 'Evaluation'],
  excerpt:
    'The hardest part of shipping an LLM feature is not building it, it is knowing whether a change made things better or worse. Without that, you are flying on vibes. Here is how I keep it honest.',
  cover: null,
  content: `
There is a moment on every LLM project where you change a prompt, the output looks a bit different, and you have absolutely no way of knowing whether you just made it better or worse. You stare at two paragraphs, decide the new one "feels sharper," and ship it. A week later something downstream breaks and you cannot tell when it regressed, because you never measured anything. I have been that person, and the cure was to stop trusting my eyes and start measuring.

Evaluation is the unglamorous backbone of every LLM feature that actually works in production. It is the difference between engineering and guessing, and most teams reach for it far too late.

## The problem with eyeballing it

Everyone starts the same way. You try a few prompts, read the outputs, decide they look good, and move on. This breaks down faster than you expect, for three specific reasons.

You cannot hold twenty examples in your head at once, so you test against a vague memory of "good." You unconsciously test the cases you already know work, which means the edge cases, the ones that actually fail, never get checked. And "good enough" silently drifts, so today's acceptable baseline quietly becomes worse than last month's with no record of the change.

The fix is the same discipline we use everywhere else in engineering. Write down what good looks like, then check against it every time, automatically.

## Build a test set before you optimize anything

Before touching prompts or swapping models, collect a set of real inputs and the outputs you would consider correct. It does not need to be huge. Thirty to fifty well-chosen examples, deliberately including the awkward edge cases, will tell you more than a thousand random ones.

This set becomes your ground truth. Every prompt change and every model swap runs against it, and you compare scores instead of impressions. The first time a "small improvement" quietly tanks five of your edge cases, you will understand exactly why this is worth the afternoon it takes to build.

\`\`\`text
test case:
  input:    "How do I cancel my subscription?"
  expects:  mentions the billing page, no made-up steps
  category: support / account
\`\`\`

## Match the measure to the task

Not everything needs a fancy evaluation. The mistake is reaching for the most sophisticated method when the simplest one captures what you care about.

- For anything with a definite answer, like classification or extraction, check exact or near matches. Simple, fast, reliable.
- For format and structure, assert on it directly. Did it return valid output with the required fields? That is a normal unit test.
- For open-ended quality, where there is no single right answer, you need judgement, and that is the only case where it gets genuinely hard.

Reach for the cheapest method that reflects the real requirement. Most teams over-engineer the easy cases and under-measure the hard one.

## Using a model to grade a model

For open-ended outputs, the practical approach is to use a strong model as a judge. You give it the question, the answer, and a clear rubric, and ask it to score against that rubric. It is not perfect, and it is consistent and it scales, which is exactly what manual review is not.

The whole trick is the rubric. Vague instructions like "rate the quality from 1 to 10" produce noise, because the model has nothing concrete to anchor on. Specific, binary criteria produce something you can actually track over time.

\`\`\`text
Weak:   "Rate the answer quality from 1 to 10."
Strong: "Is every claim in the answer supported by the provided context? yes / no
         Does the answer directly address the question asked? yes / no"
\`\`\`

Treat the judge prompt with the same care as the prompt you are trying to improve, because a sloppy judge gives you confident, meaningless numbers, which is worse than no numbers at all.

## Make evaluation part of the loop

The goal is to get to where changing a prompt feels exactly like changing code. You make the change, you run the evaluation, you watch the score move, and you keep or revert based on the number, not the feeling.

- Keep a fixed test set in version control, next to the code.
- Run it on every meaningful prompt or model change.
- Track the score over time so regressions are visible, not discovered by accident.
- Use simple checks where you can, a well-built judge only where you must.

That single habit turns prompt work from guesswork into engineering. It is the difference between a feature you can confidently improve and one you are quietly afraid to touch, and on anything that reaches real users, that confidence is the entire game.
`,
};

export default post;
